import { UploadApi, UploadedVideo } from '../../../services/videoUploads';

export type UploadSource = Pick<File, 'name' | 'size' | 'type' | 'slice'>;

export type PutPart = (
  url: string,
  body: Blob,
  onProgress: (loaded: number) => void,
  signal: AbortSignal
) => Promise<string>;

export class UploadCancelled extends Error {
  constructor() {
    super('Upload cancelled');
    Object.setPrototypeOf(this, UploadCancelled.prototype);
  }
}

export class PartUploadError extends Error {
  status: number;
  retryable: boolean;

  constructor(message: string, status: number, retryable: boolean) {
    super(message);
    this.status = status;
    this.retryable = retryable;
    Object.setPrototypeOf(this, PartUploadError.prototype);
  }
}

export type UploadOptions = {
  file: UploadSource;
  sessionId: number;
  sortOrder: number;
  api: UploadApi;
  putPart: PutPart;
  signal: AbortSignal;
  concurrency?: number;
  maxAttempts?: number;
  retryDelayMs?: number;
  onProgress?: (uploaded: number, total: number) => void;
};

export const partLength = (partNumber: number, partSize: number, size: number) =>
  Math.min(partNumber * partSize, size) - (partNumber - 1) * partSize;

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });

const isRetryable = (error: unknown) => !(error instanceof PartUploadError) || error.retryable;

export async function uploadMultipart(options: UploadOptions): Promise<UploadedVideo> {
  const { file, sessionId, sortOrder, api, putPart, signal, onProgress } = options;
  const concurrency = options.concurrency || 4;
  const maxAttempts = options.maxAttempts || 5;
  const retryDelayMs = options.retryDelayMs === undefined ? 1000 : options.retryDelayMs;

  const upload = await api.create({
    sessionId,
    filename: file.name,
    size: file.size,
    contentType: file.type || 'application/octet-stream',
  });
  const length = (partNumber: number) => partLength(partNumber, upload.partSize, file.size);

  const etags = new Map<number, string>();
  const inFlight = new Map<number, number>();
  const report = () => {
    let bytes = 0;
    etags.forEach((_, partNumber) => (bytes += length(partNumber)));
    inFlight.forEach((loaded) => (bytes += loaded));
    onProgress?.(bytes, file.size);
  };

  report();

  const queue = [...upload.parts].sort((a, b) => a.partNumber - b.partNumber);

  const stop = new AbortController();
  const stopOnCancel = () => stop.abort();
  signal.addEventListener('abort', stopOnCancel);

  const uploadPart = async (partNumber: number, url: string) => {
    for (let attempt = 1; ; attempt++) {
      if (stop.signal.aborted) {
        throw new UploadCancelled();
      }

      try {
        const begin = (partNumber - 1) * upload.partSize;
        const body = file.slice(begin, begin + length(partNumber));
        const etag = await putPart(
          url,
          body,
          (loaded) => {
            inFlight.set(partNumber, loaded);
            report();
          },
          stop.signal
        );
        inFlight.delete(partNumber);
        etags.set(partNumber, etag);
        report();
        return;
      } catch (error) {
        inFlight.delete(partNumber);
        report();

        if (stop.signal.aborted) {
          throw new UploadCancelled();
        }
        if (!isRetryable(error) || attempt >= maxAttempts) {
          throw error;
        }

        await sleep(retryDelayMs * Math.pow(2, attempt - 1), stop.signal);
      }
    }
  };

  const worker = async () => {
    for (let part = queue.shift(); part !== undefined; part = queue.shift()) {
      await uploadPart(part.partNumber, part.url);
    }
  };

  try {
    const workers = Array.from({ length: Math.min(concurrency, Math.max(queue.length, 1)) }, worker);
    await Promise.all(
      workers.map((running) =>
        running.catch((error) => {
          stop.abort();
          throw error;
        })
      )
    );
  } catch (error) {
    if (signal.aborted) {
      throw new UploadCancelled();
    }
    throw error;
  } finally {
    signal.removeEventListener('abort', stopOnCancel);
  }

  if (signal.aborted) {
    throw new UploadCancelled();
  }

  const parts = Array.from(etags.entries())
    .sort(([a], [b]) => a - b)
    .map(([partNumber, eTag]) => ({ partNumber, eTag }));

  return api.createVideo({ sessionId, sortOrder, key: upload.key, uploadId: upload.uploadId, size: file.size, parts });
}
